import { useEffect, useRef, useState, type ReactElement } from "react";
import { Button } from "./Button.tsx";
import { Menu, type MenuEntry } from "./Menu.tsx";
import { logical } from "./metrics.ts";

/**
 * One choice of a dropdown: its value and the words it shows.
 */
export interface DropdownOption<T extends string> {
  readonly value: T;
  readonly label: string;
}

/**
 * The logical pixels between a dropdown's button and the menu it opens under it.
 */
export const DROPDOWN_MENU_GAP = 1;

/**
 * The menu items a dropdown opens: every option, the current one checked (a `menuitemradio`, led by the check).
 *
 * @param options The options, in order.
 * @param value The current value, or null for none.
 * @returns The items.
 */
export function dropdownEntries<T extends string>(options: readonly DropdownOption<T>[], value: T | null): MenuEntry<T>[] {
  return options.map((option) => ({ value: option.value, label: option.label, checked: option.value === value }));
}

/**
 * A dropdown: one choice of a few, shown as the kit's secondary button that names the current option and ends with
 * the 8 px caret, which opens the kit's menu of every option under it, the current one checked. The design draws no
 * dropdown; it is made from the kit's Button and Menu (ADR-061's note for P1.9-T175), so it keeps their looks and keys:
 * the menu focuses its first item, the arrow keys, Home and End move, Enter, Space or a click chooses, and Escape or
 * Tab closes it, focus going back to the button (Tab then moving on); a press anywhere outside closes it too. The
 * button is as wide as its widest option, so choosing one moves nothing beside it, and is named by the dropdown's
 * label with the current option.
 *
 * @param props Its `label`; its `options`; the current `value`; `onChange`, told the option chosen; and
 *   `describedBy`, the id of an element that describes it, when it has one.
 * @returns The dropdown.
 * @throws Error when opened outside a `KitInputProvider`.
 */
export function Dropdown<T extends string>({ label, options, value, onChange, describedBy }: { readonly label: string; readonly options: readonly DropdownOption<T>[]; readonly value: T | null; readonly onChange: (value: T) => void; readonly describedBy?: string | undefined }): ReactElement {
  const [open, setOpen] = useState(false);
  const box = useRef<HTMLDivElement>(null);
  const current = options.find((option) => option.value === value);

  useEffect(() => {
    if (!open) {
      return undefined;
    }
    /**
     * Closes the menu on a press outside the dropdown.
     *
     * @param event The press.
     */
    const pressed = (event: PointerEvent): void => {
      if (!(event.target instanceof Node) || box.current?.contains(event.target) !== true) {
        setOpen(false);
      }
    };
    window.addEventListener("pointerdown", pressed, true);
    return () => window.removeEventListener("pointerdown", pressed, true);
  }, [open]);

  return (
    <div className="ot-dropdown" data-kit="dropdown" ref={box}>
      <Button selected={open} menuOpen={open} label={current === undefined ? label : `${label}: ${current.label}`} describedBy={describedBy} onClick={() => setOpen(!open)}>
        <span className="ot-dropdown__value">
          {options.map((option) => (
            <span key={option.value} className="ot-dropdown__option" aria-hidden={option.value === value ? undefined : true} data-current={option.value === value ? "" : undefined}>
              {option.label}
            </span>
          ))}
        </span>
      </Button>
      {open ? (
        <div className="ot-dropdown__menu" data-kit="dropdown-menu" style={{ top: `calc(100% + ${logical(DROPDOWN_MENU_GAP)})` }}>
          <Menu
            label={label}
            items={dropdownEntries(options, value)}
            onSelect={(chosen) => {
              setOpen(false);
              onChange(chosen);
            }}
            onClose={() => setOpen(false)}
          />
        </div>
      ) : null}
    </div>
  );
}
